import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import { supabase } from './supabaseClient';
import './App.css';
import Empleados from './components/Empleados';
import Reactivacion from './components/Reactivacion';
import RegistrarVacaciones from './components/RegistrarVacaciones';
import RemoverVacaciones from './components/RemoverVacaciones';
import Historial from './components/Historial';
import ChangePassword from './components/ChangePassword';
import AdminUsuarios from './components/AdminUsuarios';
import UsuariosEmpleados from './components/UsuariosEmpleados';
import AccessDenied from './components/AccessDenied';
import RequestVacations from './components/RequestVacations';
import RequestViewer from './components/RequestViewer';
import EmployeeInfo from './components/EmployeeInfo';
import ResetPassword from './components/ResetPassword';

function PrivateRoute({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return session ? children : <Navigate to="/" />;
}

function AdminRoute({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data, error } = await supabase
            .from('users')
            .select('role')
            .eq('wsp_email', user.email)
            .single();

          if (error) throw error;
          setIsAdmin(data.role === 'admin');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  if (loading) {
    return <div>Verificando permisos...</div>;
  }

  return isAdmin ? children : <AccessDenied />;
}

function AdminOrRHRoute({ children }) {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccessStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data, error } = await supabase
            .from('users')
            .select('role')
            .eq('wsp_email', user.email)
            .single();

          if (error) throw error;
          // Allow access if role is either admin or RH
          setHasAccess(data.role === 'admin' || data.role === 'RH');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking access status:', error);
        setLoading(false);
      }
    };

    checkAccessStatus();
  }, []);

  if (loading) {
    return <div>Verificando permisos...</div>;
  }

  return hasAccess ? children : <AccessDenied />;
}

function ManagerRoute({ children }) {
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkManagerStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data, error } = await supabase
            .from('users')
            .select('role')
            .eq('wsp_email', user.email)
            .single();

          if (error) throw error;
          // Allow access if role is NOT "Empleado"
          setIsManager(data.role !== 'Empleado');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking manager status:', error);
        setLoading(false);
      }
    };

    checkManagerStatus();
  }, []);

  if (loading) {
    return <div>Verificando permisos...</div>;
  }

  return isManager ? children : <AccessDenied />;
}

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/registrar-vacaciones"
            element={
              <PrivateRoute>
                <AdminOrRHRoute>
                  <RegistrarVacaciones />
                </AdminOrRHRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/empleados"
            element={
              <PrivateRoute>
                <ManagerRoute>
                  <Empleados />
                </ManagerRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/remover-vacaciones"
            element={
              <PrivateRoute>
                <AdminOrRHRoute>
                  <RemoverVacaciones />
                </AdminOrRHRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/reactivacion"
            element={
              <PrivateRoute>
                <AdminOrRHRoute>
                  <Reactivacion />
                </AdminOrRHRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/historial"
            element={
              <PrivateRoute>
                <Historial />
              </PrivateRoute>
            }
          />
          <Route
            path="/cambiar-password"
            element={
              <PrivateRoute>
                <ChangePassword />
              </PrivateRoute>
            }
          />
          <Route
            path="/usuarios-empleados"
            element={
              <PrivateRoute>
                <AdminOrRHRoute>
                  <UsuariosEmpleados />
                </AdminOrRHRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin-usuarios"
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AdminUsuarios />
                </AdminRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/solicitar-vacaciones"
            element={
              <PrivateRoute>
                <RequestVacations />
              </PrivateRoute>
            }
          />
          <Route
            path="/request-viewer"
            element={
              <PrivateRoute>
                <RequestViewer />
              </PrivateRoute>
            }
          />
          <Route
            path="/consultar-informacion"
            element={
              <PrivateRoute>
                <EmployeeInfo />
              </PrivateRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;